import Title from '../../common/Title/Title';
import Section from '../../common/Section/Section';
import { Input } from '../../common/Input/Input';
import Button from '../../common/Button/Button';
import SelectComponent from '../../common/Select/Select';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './UserDetail.module.scss';
import {
  useCreateUserMutation,
  useGetUserAffiliateCodesQuery,
  useGetUserCountryCodesQuery,
  useGetUserTypesQuery,
  useGetUsersQuery,
  useLazyGetUserDetailsByNativeIdQuery,
  useUpdateUserMutation,
} from '../../../redux/services/usersAPI';
import {
  CreateUserRequest,
  GroupRecord,
  OptionRecord,
  TeamRecord,
  UpdateUserRequest,
  UserDelegationRecord,
  UserDetailRecord,
  UserTypeOptionRecord,
} from '../../../redux/services/types/types';
import { useGetTeamsQuery } from '../../../redux/services/teamsAPI';
import { useGetUserDelegationsQuery } from '../../../redux/services/userDelegationsAPI';
import { useEffect, useMemo, useState } from 'react';
import { useForm, Controller, Control } from 'react-hook-form';
import { useGetGroupsQuery } from '../../../redux/services/groupsAPI';
import Confirm from '../../common/Confirm/Confirm';
import { cn } from '../../../utils/utils';
import { EMAIL_REGEX, GENERAL_STATUS, USER_STATUS } from '../../../constants/constants';
import Alert from '../../common/Alert/Alert';
import { useToast } from '../../common/Toast/ToastContext';
import { CancelButton } from '../../common/FormUtils/FormUtils';

type UserDelegationOption = UserDelegationRecord & { fullName?: string };

type UserDetailForm = Omit<CreateUserRequest, 'nativeId'> & { status?: number };

const initialFormState: UserDetailForm = {
  fullName: '',
  affiliateCode: '',
  countryCode: '',
  email: '',
  alternatePhone: '',
  uplineNativeId: '',
  userType: '',
  teamId: '',
  status: 1,
  userDelegations: [],
  userPermissionGroups: [],
};
export const UserDetail = () => {
  const [params] = useSearchParams();
  const userNativeId = params.get('nativeId');

  const { addToast } = useToast();
  const navigate = useNavigate();

  const [statusList, setStatusList] = useState([] as Array<any>);

  const { refetch: refetchUsers } = useGetUsersQuery();
  const [
    getUserDetailsByNativeId,
    { data: editUser, isFetching: editUserLoading, error: editError },
  ] = useLazyGetUserDetailsByNativeIdQuery();

  const editMode = !!editUser && !editError;

  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();

  const { register, handleSubmit, control, formState, reset } = useForm<UserDetailForm>({
    defaultValues: initialFormState,
  });

  const onSubmit = async (data: UserDetailForm) => {
    if (!userNativeId) throw new Error('NativeId not available !');

    const isNewUser = !editMode && !editUser;
    try {
      const res = isNewUser
        ? await createUser({
            ...data,
            nativeId: userNativeId,
          }).unwrap()
        : await updateUser(mapUserDetailsToUpdateUserRequest(data, editUser)).unwrap();

      addToast({
        content: `User ${isNewUser ? 'added' : 'updated'} successfully !`,
        type: 'success',
      });
      reset(res);
      refetchUsers();
      navigate('/user');
    } catch (err) {
      addToast({
        content: (
          <Alert
            title={`Error ${isNewUser ? 'creating' : 'updating'} user`}
            message={(err as Error).message}
            type="error"
          />
        ),
        type: 'error',
      });
    }
  };

  // Fetch user details in case of edit user
  useEffect(() => {
    if (!userNativeId) {
      setStatusOptionList();
      return;
    }
    getUserDetailsByNativeId(userNativeId).then((res) => {
      if (res.data) {
        reset(res.data);

        if (res.data.status === USER_STATUS.INACTIVE) {
          setStatusOptionList(true);
        } else {
          setStatusOptionList();
        }
      } else reset(initialFormState);
    });
  }, [userNativeId, getUserDetailsByNativeId, reset]);

  const setStatusOptionList = (isInactive: boolean = false) => {
    if (isInactive) {
      setStatusList([
        { label: 'Inactive', value: USER_STATUS.INACTIVE },
        { label: 'Active', value: USER_STATUS.ACTIVE },
      ]);
    } else {
      setStatusList([
        { label: 'Active', value: USER_STATUS.ACTIVE },
        { label: 'Suspended', value: USER_STATUS.SUSPENDED },
      ]);
    }
  };

  const renderForm = () => {
    if (!userNativeId) return null;

    if (editUserLoading) {
      return 'Loading user details...';
    }

    return (
      <form onSubmit={handleSubmit(onSubmit, console.error)} data-testid="user-detail-form">
        <Section text="User Information">
          <div className="h-1"></div>
          <div className={`${styles['input-row']} row`}>
            <div className="column-three">
              <Input
                id="fullName"
                label={'Full Name *'}
                readOnly={false}
                error={formState.errors.fullName?.message}
                {...register('fullName', {
                  required: { value: true, message: 'Full name is required !' },
                })}
              />
            </div>
            <div className="column-three">
              <Input
                id="email"
                label={'Email *'}
                readOnly={false}
                error={formState.errors.email?.message}
                {...register('email', {
                  required: { value: true, message: 'Email is required !' },
                  pattern: {
                    value: EMAIL_REGEX,
                    message: 'Invalid email !',
                  },
                })}
              />
            </div>
            <div className="column-three">
              <Input
                id="uplineNativeId"
                label={'Upline *'}
                readOnly={false}
                error={formState.errors.uplineNativeId?.message}
                {...register('uplineNativeId', {
                  required: { value: true, message: 'Upline is required !' },
                })}
              />
            </div>
          </div>
          <div className="h-1"></div>
          <div className={`${styles['input-row']} row`}>
            <div className="column-three">
              <CountrySelect control={control} />
            </div>
            <div className="column-three">
              <AffiliateCodeSelect control={control} />
            </div>
            <div className="column-three">
              <UserTypeSelect control={control} />
            </div>
          </div>
          <div className="h-1"></div>
          <div className={`${styles['input-row']} row`}>
            <div className="column-three">
              <Input
                id="alternatePhone"
                label={'Alternate Phone'}
                readOnly={false}
                error={formState.errors.alternatePhone?.message}
                {...register('alternatePhone')}
              />
            </div>
            <div className="column-three">
              {editMode && <StatusSelect control={control} statusList={statusList} />}
            </div>
            <div className="column-three"></div>
          </div>
        </Section>
        <div className="h-3"></div>
        <Section text={'Team Information'}>
          <div className="h-1"></div>
          <div className={`row ${styles['input-row']}`}>
            <div className="column-three">
              <TeamsSelect control={control} />
            </div>
          </div>
        </Section>
        <div className="h-3"></div>
        <Section text={'Delegation Information'}>
          <div className="h-1"></div>
          <div className={`row ${styles['input-row']}`}>
            <div className="column-one">
              <UserDelegationsSelect control={control} />
            </div>
          </div>
        </Section>
        <div className="h-3"></div>
        <Section text={'Assign Group'}>
          <div className="h-1"></div>
          <div className={`row ${styles['input-row']}`}>
            <div className="column-one">
              <GroupsSelect control={control} />
            </div>
          </div>
        </Section>
        <div className="h-4" />
        <div className={styles['actions']}>
          <CancelButton showAlert={formState.isDirty} onCancel={() => navigate(-1)} />
          <Button
            text={isCreating || isUpdating ? 'Loading ...' : 'Save'}
            color="primary-black"
            disabled={!formState.isDirty}
          />
        </div>
        <div className="h-3" />
      </form>
    );
  };

  const getTitleText = () => {
    if (!userNativeId || editUserLoading) return 'Add / Edit User';
    return editMode ? 'Edit User' : 'Add User';
  };

  return (
    <div className="container">
      <Title text={getTitleText()} />
      <div className="h-3" />
      <NativeIdForm />
      <div className="h-3"></div>
      {renderForm()}
    </div>
  );
};

const NativeIdForm = () => {
  const navigate = useNavigate();

  const [params] = useSearchParams();
  const selectedNativeId = params.get('nativeId');

  const { register, handleSubmit, formState } = useForm<{ nativeId: string }>({
    defaultValues: {
      nativeId: selectedNativeId ?? '',
    },
  });

  const onSubmit = ({ nativeId }: { nativeId: string }) => {
    navigate(`/user/detail?nativeId=${nativeId}`);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, console.error)}>
      <div className={cn('row', styles['input-row'], styles['native-id-container'])}>
        <div className="column-three">
          <Input
            id="nativeId"
            label={'Native ID'}
            readOnly={false}
            error={formState.errors.nativeId?.message}
            {...register('nativeId', {
              required: { value: true, message: 'Native Id is required !' },
            })}
            disabled={!!selectedNativeId}
          />
        </div>
        <div className="column-three">
          <Button
            color="primary-black"
            border
            size="normal"
            text="Retrieve User from LDAP"
            disabled={!!selectedNativeId}
            data-testid="retrieve-user-button"
          />
        </div>
        <div className="column-three"></div>
      </div>
    </form>
  );
};

const CountrySelect = ({ control }: { control: Control<UserDetailForm> }) => {
  const { data: countryCodes } = useGetUserCountryCodesQuery();

  return (
    <Controller
      control={control}
      name="countryCode"
      rules={{
        required: { value: true, message: 'Country Code is required !' },
      }}
      render={({ field, fieldState }) => {
        const { value, onChange, ref, ...otherProps } = field;
        return (
          <SelectComponent<OptionRecord>
            isMulti={false}
            label={'Country Code *'}
            options={countryCodes}
            value={countryCodes?.find((v) => v.code === value) ?? null}
            onChange={(val) => onChange(val ? val.code : null)}
            error={fieldState.error?.message}
            getOptionLabel={(data) => data.code}
            getOptionValue={(data) => data.code}
            inputId="countryCode"
            selectRef={ref}
            {...otherProps}
          />
        );
      }}
    />
  );
};

const UserTypeSelect = ({ control }: { control: Control<UserDetailForm> }) => {
  const { data: userTypes } = useGetUserTypesQuery();

  return (
    <Controller
      control={control}
      name="userType"
      rules={{
        required: { value: true, message: 'User Type is required !' },
      }}
      render={({ field, fieldState }) => {
        const { value, onChange, ref, ...otherProps } = field;
        return (
          <SelectComponent<UserTypeOptionRecord>
            isMulti={false}
            label={'User Type *'}
            options={userTypes}
            value={userTypes?.find((v) => v.code === String(value)) ?? null}
            onChange={(val) => onChange(val ? val.code : null)}
            error={fieldState.error?.message}
            getOptionLabel={(data) => data.value}
            getOptionValue={(data) => data.code}
            selectRef={ref}
            inputId="userType"
            {...otherProps}
          />
        );
      }}
    />
  );
};

const AffiliateCodeSelect = ({ control }: { control: Control<UserDetailForm> }) => {
  const { data: affiliateCodes } = useGetUserAffiliateCodesQuery();

  return (
    <Controller
      control={control}
      name="affiliateCode"
      rules={{
        required: { value: true, message: 'Affiliate Code is required !' },
      }}
      render={({ field, fieldState }) => {
        const { value, onChange, ref, ...otherProps } = field;
        return (
          <SelectComponent<OptionRecord>
            isMulti={false}
            label={'Affiliate Code *'}
            options={affiliateCodes}
            value={affiliateCodes?.find((v) => v.code === value) ?? null}
            onChange={(val) => onChange(val ? val.code : null)}
            error={fieldState.error?.message}
            getOptionLabel={(data) => data.code}
            getOptionValue={(data) => data.code}
            selectRef={ref}
            inputId="affiliateCode"
            {...otherProps}
          />
        );
      }}
    />
  );
};

const StatusSelect = ({
  control,
  statusList,
}: {
  control: Control<UserDetailForm>;
  statusList: Array<any>;
}) => {
  const statusOptions = statusList;
  return (
    <Controller
      control={control}
      name="status"
      render={({ field, fieldState }) => {
        const { ref, value, onChange, ...otherProps } = field;
        return (
          <SelectComponent
            isMulti={false}
            label={'Status'}
            options={statusOptions}
            value={statusOptions.find((opt) => opt.value === value)}
            onChange={(val) => onChange(val ? val.value : null)}
            error={fieldState.error?.message}
            inputId="status"
            {...otherProps}
          />
        );
      }}
    />
  );
};

const TeamsSelect = ({ control }: { control: Control<UserDetailForm> }) => {
  const { data: teams } = useGetTeamsQuery();

  return (
    <Controller
      control={control}
      name="teamId"
      render={({ field, fieldState }) => {
        const { value, onChange, ref, ...otherProps } = field;
        return (
          <SelectComponent<TeamRecord>
            isMulti={false}
            label={'Team'}
            options={teams?.filter((t) => t.status !== GENERAL_STATUS.INACTIVE)}
            value={value ? teams?.find((t) => t.teamId.toString() === value) : null}
            onChange={(val) => onChange(val ? val.teamId : null)}
            getOptionLabel={(data) => data.name}
            getOptionValue={(data) => data.teamId.toString()}
            error={fieldState.error?.message}
            selectRef={ref}
            inputId="teamId"
            {...otherProps}
          />
        );
      }}
    />
  );
};

const UserDelegationsSelect = ({ control }: { control: Control<UserDetailForm> }) => {
  const { data: userDelegations } = useGetUserDelegationsQuery();
  const { data: users } = useGetUsersQuery();

  const userDelegationOptions = useMemo(() => {
    if (!userDelegations) return [];
    return userDelegations.map<UserDelegationOption>((ud) => {
      const userRecord = users?.find((u) => u.userId === ud.user_id);

      return {
        ...ud,
        fullName: userRecord?.fullName,
      };
    });
  }, [users, userDelegations]);

  return (
    <Controller
      control={control}
      name="userDelegations"
      render={({ field, fieldState }) => {
        const { value, onChange, ref, ...otherProps } = field;
        return (
          <SelectComponent<UserDelegationOption, true>
            isMulti
            label={'Delegated User'}
            options={userDelegationOptions?.filter((t) => t.status !== GENERAL_STATUS.INACTIVE)}
            value={userDelegationOptions.filter((udo) =>
              value ? value.some((v) => v.delegateUserId === udo.delegate_user_id) : false,
            )}
            onChange={(val) =>
              onChange(
                val.map((v) => ({
                  delegateUserId: v.delegate_user_id,
                })),
              )
            }
            getOptionLabel={(data) => data.fullName || data.delegate_user_id.toString()}
            getOptionValue={(data) => data.delegate_user_id.toString()}
            error={fieldState.error?.message}
            selectRef={ref}
            inputId="delegatedUser"
            {...otherProps}
          />
        );
      }}
    />
  );
};

const GroupsSelect = ({ control }: { control: Control<UserDetailForm> }) => {
  const { data: groups } = useGetGroupsQuery();

  return (
    <Controller
      control={control}
      name="userPermissionGroups"
      render={({ field, fieldState }) => {
        const { value, onChange, ref, ...otherProps } = field;
        return (
          <SelectComponent<GroupRecord, true>
            isMulti
            label={'Group'}
            options={groups?.filter((t) => t.status !== GENERAL_STATUS.INACTIVE)}
            value={groups?.filter((grp) =>
              value ? value.some((v) => v.permissionGroupId === grp.permissionGroupId) : false,
            )}
            onChange={(val) =>
              onChange(
                val.map((v) => ({
                  permissionGroupId: v.permissionGroupId,
                })),
              )
            }
            getOptionLabel={(data) => data.name}
            getOptionValue={(data) => data.permissionGroupId.toString()}
            error={fieldState.error?.message}
            selectRef={ref}
            isSearchable
            inputId="group"
            {...otherProps}
          />
        );
      }}
    />
  );
};

const mapUserDetailsToUpdateUserRequest = (
  userDetails: UserDetailForm,
  editUser: UserDetailRecord,
): UpdateUserRequest => {
  return {
    fullName: userDetails.fullName,
    affiliateCode: userDetails.affiliateCode,
    countryCode: userDetails.countryCode,
    email: userDetails.email,
    alternatePhone: userDetails.alternatePhone,
    status: userDetails.status ?? editUser.status,
    teamId: userDetails.teamId,
    uplineNativeId: userDetails.uplineNativeId,
    userType: userDetails.userType,

    nativeId: editUser.nativeId,
    uid: editUser.uid,
    userId: editUser.userId,

    userDelegations: userDetails.userDelegations?.map((ud) => {
      const existing = editUser.userDelegations?.find(
        (eud) => eud.delegateUserId === ud.delegateUserId,
      );
      if (existing) {
        return {
          ...ud,
          userDelegationId: existing.userDelegationId,
        };
      }
      return ud;
    }),
    userPermissionGroups: userDetails.userPermissionGroups?.map((grp) => {
      const existing = editUser.userPermissionGroups?.find(
        (eGrp) => eGrp.permissionGroupId === grp.permissionGroupId,
      );

      if (existing) {
        return {
          ...grp,
          userPermissionGroupId: existing.userPermissionGroupId,
        };
      }
      return grp;
    }),
  };
};

export default UserDetail;
